(() => {
  "use strict";

  const WARNING_KEY = "__anywellYS16FamilyReviewWarningShown";
  const warnClosed = (error) => {
    if (window[WARNING_KEY]) return;
    window[WARNING_KEY] = true;
    console.warn("Unable to initialize YS16 family reviews; keeping the existing review panel.", error);
  };

  const configNode = document.getElementById("aw-ys16-family-review-config");
  let config;

  try {
    config = configNode ? JSON.parse(configNode.textContent || "{}") : null;
  } catch (error) {
    warnClosed(error);
    return;
  }

  const root = document.getElementById("shopify-product-reviews");
  const appInner = root?.querySelector(".anywell-reviews__app-inner");
  const loadButton = root?.querySelector("[data-anywell-review-load-more]");
  const loadLabel = loadButton?.querySelector("[data-anywell-review-load-label]");
  const status = root?.querySelector("[data-anywell-review-status]");
  const core = window.AnywellYS16FamilyReviewCore;
  const isNonBlank = (value) => value !== undefined && value !== null && String(value).trim() !== "";
  const hasCore = core && [
    "parseReviewFragment",
    "dedupeAndRank",
    "computeAggregate",
    "getBatchBounds"
  ].every((key) => typeof core[key] === "function");

  if (
    !configNode ||
    !isNonBlank(config?.shopDomain) ||
    !isNonBlank(config?.sourceProductId) ||
    !isNonBlank(config?.currentProductId) ||
    !root ||
    !appInner ||
    !loadButton ||
    !loadLabel ||
    !status ||
    !hasCore
  ) {
    warnClosed(new Error("Required configuration, review DOM hooks, or review core are unavailable."));
    return;
  }

  if (root.dataset.anywellFamilyReviewsInitialized === "true") return;
  root.dataset.anywellFamilyReviewsInitialized = "true";

  const PER_PAGE = 25;
  const MAX_PAGES = 20;
  const parsedBatchSize = Number.parseInt(config.batchSize, 10);
  const batchSize = Number.isFinite(parsedBatchSize) && parsedBatchSize > 0 ? parsedBatchSize : 12;
  const defaultLabels = {
    loadMore: "Load more reviews",
    loading: "Loading reviews",
    retry: "Try again",
    complete: "All {count} reviews shown",
    loaded: "{count} more reviews loaded",
    recovered: "{count} live reviews available"
  };
  const state = {
    records: [],
    visible: 0,
    loading: false,
    completeFetch: false,
    errors: []
  };
  let reviewsContainer = null;
  const renderedNodes = new Map();

  const label = (key, count = 0) => String(config.labels?.[key] || defaultLabels[key] || "")
    .replace("{count}", String(count));

  const summaryHooks = () => {
    const summary = root.querySelector("[data-anywell-review-summary]");
    const value = summary?.querySelector("[data-anywell-rating-value]");
    const stars = summary?.querySelector("[data-anywell-rating-stars]");
    const count = summary?.querySelector("[data-anywell-rating-count]");
    const distribution = summary?.querySelector("[data-anywell-rating-distribution]");
    return summary && value && stars && count && distribution
      ? { summary, value, stars, count, distribution }
      : null;
  };

  const renderStarMeter = (hooks, average, count) => {
    const fixedAverage = average.toFixed(1);
    const ariaLabel = `${fixedAverage} out of 5 stars from ${count} reviews`;
    hooks.value.textContent = fixedAverage;
    hooks.count.textContent = `${count} reviews`;
    hooks.stars.setAttribute("aria-label", ariaLabel);
    hooks.stars.style.setProperty("--anywell-rating-percent", `${(average / 5) * 100}%`);
    hooks.stars.innerHTML = '<span class="anywell-reviews__star-base" aria-hidden="true">☆☆☆☆☆</span><span class="anywell-reviews__star-fill" aria-hidden="true">★★★★★</span>';
    hooks.summary.setAttribute("aria-label", ariaLabel);
    hooks.summary.hidden = false;
  };

  const renderConfiguredFallback = () => {
    const hooks = summaryHooks();
    const count = Number.parseInt(config.reviewCount, 10);
    const average = Number.parseFloat(config.rating);
    if (!hooks || !Number.isFinite(count) || count < 1 || !Number.isFinite(average) || average <= 0) return;

    renderStarMeter(hooks, average, count);
    hooks.distribution.replaceChildren();
    hooks.distribution.hidden = true;
  };

  const renderAggregate = (records) => {
    const hooks = summaryHooks();
    const aggregate = core.computeAggregate(records);
    if (aggregate.count < 1 || !Number.isFinite(aggregate.average) || aggregate.average <= 0) return;

    const legacyCount = root.querySelector(".anywell-reviews__summary:not([data-anywell-review-summary]) .anywell-reviews__count");
    if (legacyCount) legacyCount.textContent = `${aggregate.count} ${aggregate.count === 1 ? "review" : "reviews"}`;
    if (!hooks) return;

    renderStarMeter(hooks, aggregate.average, aggregate.count);
    hooks.distribution.innerHTML = [5, 4, 3, 2, 1].map((rating) => {
      const count = aggregate.distribution[rating];
      const percent = (count / aggregate.count) * 100;
      return `<div class="anywell-reviews__rating-row" data-rating-row><span>${rating} star</span><span class="anywell-reviews__rating-track"><span style="width:${percent}%"></span></span><span>${count}</span></div>`;
    }).join("");
    hooks.distribution.hidden = false;
  };

  const errorStatus = () => `Unable to load all reviews. ${label("retry")}`;

  const setState = (nextState, count = state.visible, announcement = "") => {
    state.loading = nextState === "loading";
    appInner.setAttribute("aria-busy", state.loading ? "true" : "false");
    loadButton.dataset.state = nextState;
    loadButton.disabled = state.loading;
    loadButton.hidden = nextState === "complete";
    loadLabel.textContent = label(nextState === "ready" ? "loadMore" : nextState, count);

    if (announcement) {
      status.textContent = announcement;
    } else if (nextState === "complete") {
      status.textContent = label("complete", count);
    } else if (nextState === "retry") {
      status.textContent = errorStatus();
    } else if (nextState === "loading") {
      status.textContent = label("loading", count);
    } else {
      status.textContent = "";
    }
  };

  const safeUrl = (element, attributeName, value) => {
    const trimmed = String(value || "").trim();
    const compact = trimmed.replace(/[\u0000-\u0020\u007f]+/g, "").toLowerCase();
    if (!trimmed) return false;
    if (compact.startsWith("javascript:") || compact.startsWith("vbscript:")) return false;
    if (compact.startsWith("data:")) {
      return element.tagName === "IMG" &&
        (attributeName === "src" || attributeName === "data-src") &&
        /^data:image\/(?:png|gif|jpe?g|webp|avif);base64,/i.test(compact);
    }
    try {
      return new URL(trimmed, document.baseURI).protocol === "https:";
    } catch (_error) {
      return false;
    }
  };

  const safeSrcset = (element, value) => String(value || "").split(",").every((candidate) => {
    const source = candidate.trim().split(/\s+/)[0];
    return source && !source.toLowerCase().startsWith("data:") && safeUrl(element, "src", source);
  });

  const isUrlAttribute = (name) => ["href", "src", "poster", "action", "formaction"].includes(name) ||
    /(?:^|[-:])(?:url|href|src|poster|action)$/.test(name);

  const sanitizeReviewNode = (html) => {
    const template = document.createElement("template");
    template.innerHTML = typeof html === "string" ? html : "";
    template.content.querySelectorAll("script, style, object, embed, form, link, meta, base").forEach((node) => node.remove());

    template.content.querySelectorAll("*").forEach((element) => {
      Array.from(element.attributes).forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        if (name.startsWith("on") || name === "srcdoc" || name === "style") {
          element.removeAttribute(attribute.name);
          return;
        }
        if (isUrlAttribute(name) && !safeUrl(element, name, attribute.value)) {
          element.removeAttribute(attribute.name);
          return;
        }
        if (name === "srcset" && !safeSrcset(element, attribute.value)) {
          element.removeAttribute(attribute.name);
        }
      });
    });

    template.content.querySelectorAll("iframe").forEach((iframe) => {
      const source = String(iframe.getAttribute("src") || "").trim();
      let resolvedSource;
      try {
        resolvedSource = new URL(source, document.baseURI);
      } catch (_error) {
        resolvedSource = null;
      }
      if (!source || !resolvedSource || resolvedSource.protocol !== "https:") {
        iframe.remove();
        return;
      }
      iframe.removeAttribute("srcdoc");
      iframe.setAttribute("loading", "lazy");
      iframe.setAttribute("sandbox", "allow-scripts allow-same-origin allow-presentation");
      iframe.setAttribute("allow", "fullscreen; picture-in-picture");
      iframe.setAttribute("referrerpolicy", "strict-origin-when-cross-origin");
    });

    template.content.querySelectorAll(".jdgm-rev__reply, .jdgm-rev__reply-content").forEach((reply) => {
      const hasVisibleContent = reply.textContent.trim() || reply.querySelector("img, video, iframe, a[href]");
      if (!hasVisibleContent) reply.remove();
    });

    return template.content.firstElementChild;
  };

  const normalizeRecord = (record) => {
    const card = sanitizeReviewNode(record.html);
    if (!card) return null;

    card.classList.remove("jdgm-rev--has-media");
    const reviews = document.createElement("div");
    reviews.className = "jdgm-rev-widg__reviews";
    reviews.append(card);
    const normalized = core.parseReviewFragment(document, reviews.outerHTML, record.sourceProductId)[0];
    if (!normalized) return null;

    return {
      ...normalized,
      id: record.id,
      sourceProductId: record.sourceProductId,
      sourceOrder: record.sourceOrder
    };
  };

  const hydrateMedia = (scope) => {
    scope.querySelectorAll("img").forEach((image) => {
      const source = image.getAttribute("src") || image.getAttribute("data-src");
      if (source && !image.getAttribute("src")) image.setAttribute("src", source);
      image.setAttribute("loading", "lazy");
      image.addEventListener("error", () => {
        const media = image.closest([
          ".jdgm-rev__pic-link",
          ".jdgm-rev__pics a",
          ".jdgm-rev__pics-wrapper a",
          ".jdgm-rev__picture a"
        ].join(",")) || image;
        media.remove();
      }, { once: true });
    });

    scope.querySelectorAll("video").forEach((video) => {
      video.removeAttribute("autoplay");
      video.autoplay = false;
      video.controls = true;
      video.setAttribute("controls", "");
      video.setAttribute("playsinline", "");
      video.addEventListener("error", () => {
        const wrapper = video.closest([
          ".jdgm-rev__video",
          ".jdgm-rev__video-container",
          ".jdgm-rev__video-wrapper",
          ".jdgm-video"
        ].join(","));
        const mediaCount = wrapper
          ? wrapper.querySelectorAll("video, iframe, [data-video-id], [data-video-url]").length
          : 0;
        (wrapper && mediaCount <= 1 ? wrapper : video).remove();
      }, { once: true });
    });
  };

  const syncVisibleNode = (node, record) => {
    const authoritativeNode = sanitizeReviewNode(record.html);
    if (!authoritativeNode) return;

    Array.from(node.attributes).forEach((attribute) => node.removeAttribute(attribute.name));
    Array.from(authoritativeNode.attributes).forEach((attribute) => {
      node.setAttribute(attribute.name, attribute.value);
    });
    node.dataset.anywellReviewRecordId = record.id;
    node.replaceChildren(...authoritativeNode.childNodes);
    hydrateMedia(node);
  };

  const ensureStream = () => {
    if (reviewsContainer?.isConnected) return reviewsContainer;

    const widget = document.createElement("div");
    widget.className = "jdgm-rev-widg anywell-reviews__family-stream";
    widget.innerHTML = '<div class="jdgm-rev-widg__body"><div class="jdgm-rev-widg__reviews" data-anywell-review-waterfall></div></div>';
    appInner.replaceChildren(widget);
    reviewsContainer = widget.querySelector(".jdgm-rev-widg__reviews");
    root.dataset.anywellFamilyReviewsLoaded = "true";
    root.dataset.anywellReviewSourceProductId = String(config.sourceProductId);
    return reviewsContainer;
  };

  const fetchPage = async (productId, page) => {
    const endpoint = new URL("https://api.judge.me/reviews/reviews_for_widget");
    endpoint.searchParams.set("shop_domain", config.shopDomain);
    endpoint.searchParams.set("platform", "shopify");
    endpoint.searchParams.set("per_page", String(PER_PAGE));
    endpoint.searchParams.set("page", String(page));
    endpoint.searchParams.set("product_id", String(productId));

    const response = await window.fetch(endpoint, {
      credentials: "omit",
      headers: { Accept: "application/json" }
    });
    if (!response.ok) throw new Error(`Judge.me returned ${response.status}`);
    return response.json();
  };

  const partialResult = (records, total, error) => ({
    records,
    total,
    complete: false,
    error
  });

  const fetchProductReviews = async (productId) => {
    const records = [];
    const seen = new Set();
    let expectedTotal = null;

    for (let page = 1; page <= MAX_PAGES; page += 1) {
      try {
        const payload = await fetchPage(productId, page);
        const reportedTotal = Number(payload?.total_count);
        if (Number.isFinite(reportedTotal) && reportedTotal >= 0) {
          expectedTotal = Math.floor(reportedTotal);
        }

        const parsed = core.parseReviewFragment(document, payload?.html || "", productId)
          .map(normalizeRecord)
          .filter(Boolean);
        let newRecords = 0;
        parsed.forEach((record) => {
          if (seen.has(record.id)) return;
          seen.add(record.id);
          records.push(record);
          newRecords += 1;
        });

        if (expectedTotal === 0 || (expectedTotal !== null && seen.size >= expectedTotal)) {
          return { records, total: expectedTotal || 0, complete: true, error: null };
        }
        if (parsed.length === 0 || newRecords === 0) {
          return partialResult(
            records,
            expectedTotal,
            new Error("Judge.me paging stopped before the reported total.")
          );
        }
      } catch (error) {
        return partialResult(records, expectedTotal, error);
      }
    }

    return partialResult(
      records,
      expectedTotal,
      new Error("Judge.me paging exceeded the defensive limit.")
    );
  };

  const mergePartialRecords = (incoming) => {
    const ranked = core.dedupeAndRank([...state.records, ...incoming]);
    if (state.visible === 0) return ranked;

    const visibleRecords = state.records.slice(0, state.visible);
    const visibleIds = new Set(visibleRecords.map((record) => record.id));
    return [
      ...visibleRecords,
      ...ranked.filter((record) => !visibleIds.has(record.id))
    ];
  };

  const reconcileAuthoritativeRecords = (incoming) => {
    const ranked = core.dedupeAndRank(incoming);
    if (state.visible === 0) return ranked;

    const authoritativeById = new Map(ranked.map((record) => [record.id, record]));
    const survivingVisible = [];

    state.records.slice(0, state.visible).forEach((record) => {
      const authoritativeRecord = authoritativeById.get(record.id);
      const node = renderedNodes.get(record.id);

      if (!authoritativeRecord) {
        node?.remove();
        renderedNodes.delete(record.id);
        return;
      }
      if (!node?.isConnected) {
        renderedNodes.delete(record.id);
        return;
      }

      syncVisibleNode(node, authoritativeRecord);
      survivingVisible.push(authoritativeRecord);
    });

    state.visible = survivingVisible.length;
    const survivingIds = new Set(survivingVisible.map((record) => record.id));
    return [
      ...survivingVisible,
      ...ranked.filter((record) => !survivingIds.has(record.id))
    ];
  };

  const appendBatch = (recoveredCount = null) => {
    const bounds = core.getBatchBounds(state.visible, state.records.length, batchSize);
    const added = bounds.end - bounds.start;

    if (added > 0) {
      const batch = state.records.slice(bounds.start, bounds.end);
      const fragment = document.createDocumentFragment();
      batch.forEach((record) => {
        const node = sanitizeReviewNode(record.html);
        if (!node) return;
        node.dataset.anywellReviewRecordId = record.id;
        renderedNodes.set(record.id, node);
        hydrateMedia(node);
        fragment.append(node);
      });
      ensureStream().append(fragment);
      state.visible = bounds.end;
    }

    const hasAvailableBacklog = state.visible < state.records.length;
    const nextState = hasAvailableBacklog
      ? "ready"
      : state.errors.length
        ? "retry"
        : "complete";
    const recoveredAnnouncement = recoveredCount === null ? "" : label("recovered", recoveredCount);
    const announcement = recoveredAnnouncement
      ? nextState === "complete"
        ? `${recoveredAnnouncement}. ${label("complete", state.visible)}`
        : recoveredAnnouncement
      : nextState === "ready"
        ? label("loaded", added)
        : nextState === "retry" && added > 0
          ? `${label("loaded", added)}. ${errorStatus()}`
          : "";
    setState(nextState, state.visible, announcement);
  };

  const loadAll = async () => {
    if (state.loading) return;
    const recovering = loadButton.dataset.state === "retry";
    setState("loading", state.visible);

    const productIds = [...new Set(
      [config.sourceProductId, config.currentProductId]
        .filter((productId) => productId !== undefined && productId !== null && String(productId).trim())
        .map(String)
    )];
    const results = await Promise.all(productIds.map(fetchProductReviews));
    state.errors = results.filter((result) => result.error).map((result) => result.error);
    state.completeFetch = results.every((result) => result.complete);
    const incoming = results.flatMap((result) => result.records);
    state.records = state.completeFetch
      ? reconcileAuthoritativeRecords(incoming)
      : mergePartialRecords(incoming);

    if (state.records.length === 0) {
      if (state.errors.length === 0) {
        state.errors.push(new Error("No live review data is available."));
      }
      setState("retry", 0);
      return;
    }

    if (state.completeFetch) renderAggregate(state.records);

    if (state.visible === 0) {
      appendBatch(recovering && state.completeFetch ? state.records.length : null);
      return;
    }

    const hasAvailableBacklog = state.visible < state.records.length;
    const nextState = hasAvailableBacklog
      ? "ready"
      : state.errors.length
        ? "retry"
        : "complete";
    const recoveredAnnouncement = recovering && state.completeFetch
      ? label("recovered", state.records.length)
      : "";
    const announcement = recoveredAnnouncement && nextState === "complete"
      ? `${recoveredAnnouncement}. ${label("complete", state.visible)}`
      : recoveredAnnouncement;
    setState(nextState, state.visible, announcement);
  };

  loadButton.addEventListener("click", () => {
    if (loadButton.dataset.state === "retry") {
      loadAll();
    } else if (loadButton.dataset.state === "ready") {
      appendBatch();
    }
  });

  renderConfiguredFallback();

  if ("IntersectionObserver" in window) {
    const observer = new window.IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      loadAll();
    }, { rootMargin: "500px 0px" });
    observer.observe(appInner);
  } else {
    loadAll();
  }
})();
